
import { Flex,StatusText,Tag,Tooltip,Typography } from "../../../../../../../shared/frontend/ui";
import { BankOrderState, transferOrderStateLabels } from "../../../app/api/bankOrders";
import { spacing } from "../../../app/design-system";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import { formatDateOnly,formatMoney } from "../../../app/units/format";
import type { UnitsSettings } from "../../../app/units/units";
import { paymentMethodLabel } from "../../../app/utils/paymentMethod";
import type { ColumnDefinition } from "../../../app/utils/useProTableColumnEditor";
import { paymentReferenceDisplayValue } from "./paymentReferenceDisplay";
import { personPaymentDescription } from "./personPaymentDisplay";
import type { PaymentRow } from "./personDetailTypes";
import type { ProColumnsWithMeasure } from "../../../components/SettingsListPage";
import { ISO_DATE_LENGTH } from "../../../app/utils/isoDate";
import { PAYMENT_AMOUNT_TOLERANCE, PAYMENT_CANCELLATION_TYPE_REFUND, PAYMENT_DISPLAY_STATUS, PAYMENT_ROW_KIND_DEBT_COLLECTION } from "./personDetailValues";

type TextFn = I18nContextValue["text"];

export const PERSON_PAYMENT_COLUMN_DEFINITIONS: ColumnDefinition[] = [
  { key: "paidDate", label: "Date", defaultVisible: true },
  { key: "amount", label: "Amount", defaultVisible: true },
  { key: "status", label: "Status", defaultVisible: true },
  { key: "description", label: "Payment allocation", defaultVisible: true },
  { key: "method", label: "Payment method", defaultVisible: true },
  { key: "reference", label: "Reference", defaultVisible: true }
];

function paymentDisplayStatus(row: PaymentRow): string {
  // Corrections change the contribution balance, not the historical receipt.
  if (Number(row.amount || 0) > 0 && !row.rowKind
    && !row.isReversal && !row.reversesPaymentId && row.displayStatus !== PAYMENT_DISPLAY_STATUS.rebooked) return PAYMENT_DISPLAY_STATUS.paid;
  const dtoStatus = String(row.displayStatus || "").trim();
  if (dtoStatus) return dtoStatus;

  const isReversal = row.isReversal === true || (!!row.reversesPaymentId && Number(row.amount || 0) < 0);
  if (isReversal) return PAYMENT_DISPLAY_STATUS.reversal;
  if (row.isCancelled) return PAYMENT_DISPLAY_STATUS.cancelled;
  const amount = Number(row.amount || 0);
  if (amount < 0) return PAYMENT_DISPLAY_STATUS.refund;
  const lineNet = row.lineNet != null ? Number(row.lineNet) : amount;
  if (amount > 0 && lineNet <= PAYMENT_AMOUNT_TOLERANCE && row.canVoid === false) return PAYMENT_DISPLAY_STATUS.reversed;
  return PAYMENT_DISPLAY_STATUS.paid;
}

function renderPaymentStatus(row: PaymentRow, text: TextFn) {
  const status = paymentDisplayStatus(row);
  switch (status) {
    case PAYMENT_DISPLAY_STATUS.reversal:
      // Eine abgelehnte oder unklare Überweisung der Rückerstattung ist die Abweichung, die zählt.
      if (row.transferOrderState === BankOrderState.Rejected || row.transferOrderState === BankOrderState.Unknown
        || row.transferOrderState === BankOrderState.Submitting)
        return <StatusText color={row.transferOrderState === BankOrderState.Rejected ? "red" : "orange"}>
          {text(transferOrderStateLabels[row.transferOrderState])}</StatusText>;
      return <Tag color="orange">{text(row.cancellationType === 1 ? "Payment chargeback line"
        : row.cancellationType === PAYMENT_CANCELLATION_TYPE_REFUND ? "Payment refund" : "Payment reversal line")}</Tag>;
    case PAYMENT_DISPLAY_STATUS.partiallyReversed:
      return <Tag color="gold">{text("Payment partially voided")}</Tag>;
    case PAYMENT_DISPLAY_STATUS.reversed:
      return <Tag>{text("Payment fully reversed")}</Tag>;
    case PAYMENT_DISPLAY_STATUS.rebooked:
      return <Tag color="blue">{text("Payment rebooked")}</Tag>;
    case PAYMENT_DISPLAY_STATUS.cancelled:
      return <Tag color="red">{text("Payment status reversed")}</Tag>;
    case PAYMENT_DISPLAY_STATUS.refund:
      return <Tag color="purple">{text("Payment refund")}</Tag>;
    case PAYMENT_DISPLAY_STATUS.debtCollection:
      return <Tag color="geekblue">{text("Debt collection (status)")}</Tag>;
    case PAYMENT_DISPLAY_STATUS.paid:
    default:
      return <Tag color="green">{text("Paid (status)")}</Tag>;
  }
}

function renderPaymentReference(reference: string | null | undefined, text: TextFn) {
  const normalizedReference = String(reference || "").trim();
  const displayValue = paymentReferenceDisplayValue(reference);
  if (!displayValue) return <Typography.Text type="secondary">{text("\u2014")}</Typography.Text>;

  return (
    <Tooltip title={normalizedReference}>
      <Typography.Text className="person-payment-reference" strong={displayValue !== normalizedReference}>
        {displayValue}
      </Typography.Text>
    </Tooltip>
  );
}

type PersonPaymentColumnsOptions = {
  text: TextFn;
  units: UnitsSettings;
  paymentMethodNameByType: Record<number, string>;
};

export function createPersonPaymentColumns({
  text,
  units,
  paymentMethodNameByType
}: PersonPaymentColumnsOptions): Record<string, ProColumnsWithMeasure<PaymentRow>> {
  return {
    paidDate: {
      title: text("Date"),
      dataIndex: "paidDate",
      key: "paidDate",
      ellipsis: false,
      defaultSortOrder: "descend",
      sorter: (a, b) => {
        const paidCompare = String(a.paidDate || "").localeCompare(String(b.paidDate || ""));
        if (paidCompare !== 0) return paidCompare;
        return String(a.bookedAt || "").localeCompare(String(b.bookedAt || ""));
      },
      render: (_, row) => formatDateOnly(String(row.paidDate || "").slice(0, ISO_DATE_LENGTH), units)
    },
    amount: {
      title: text("Amount"),
      dataIndex: "amount",
      key: "amount",
      align: "right",
      ellipsis: false,
      render: (_, row) => formatMoney(Number(row.amount || 0), units)
    },
    status: {
      title: text("Status"),
      dataIndex: "displayStatus",
      key: "status",
      sortValue: (row) => paymentDisplayStatus(row),
      ellipsis: false,
      render: (_, row) => renderPaymentStatus(row, text)
    },
    description: {
      title: text("Payment allocation"),
      key: "description",
      ellipsis: false,
      sortValue: (row) => personPaymentDescription(row, text),
      render: (_, row) => {
        const description = personPaymentDescription(row, text);
        if (description) return <Typography.Text>{description}</Typography.Text>;
        return <Typography.Text type="secondary">{text("\u2014")}</Typography.Text>;
      }
    },
    dueDate: {
      title: text("Due date"),
      dataIndex: "dueDate",
      key: "dueDate",
      ellipsis: false,
      render: (_, row) =>
        row.dueDate
          ? formatDateOnly(String(row.dueDate).slice(0, ISO_DATE_LENGTH), units)
          : <Typography.Text type="secondary">{text("\u2014")}</Typography.Text>
    },
    method: {
      title: text("Payment method"),
      dataIndex: "method",
      key: "method",
      ellipsis: false,
      render: (_, row) => {
        if (row.rowKind === PAYMENT_ROW_KIND_DEBT_COLLECTION && row.debtCollectionAgencyName) {
          return text("Debt collection with agency").replace("{0}", String(row.debtCollectionAgencyName));
        }
        return paymentMethodLabel(row.method, paymentMethodNameByType);
      }
    },
    reference: {
      title: text("Reference"),
      dataIndex: "reference",
      key: "reference",
      ellipsis: false,
      render: (_, row, __, action) => {
        if ((action as { cardView?: boolean } | undefined)?.cardView) {
          return paymentReferenceDisplayValue(row.reference) || <Typography.Text type="secondary">{text("\u2014")}</Typography.Text>;
        }
        return renderPaymentReference(row.reference, text);
      }
    }
  };
}

/** Receivable allocation details are a different schema from a payment receipt. */
export function createPersonPaymentAllocationColumns({ text, units }: Pick<PersonPaymentColumnsOptions, "text" | "units">): ProColumnsWithMeasure<PaymentRow>[] {
  return [
    {
      title: text("Due date"),
      key: "dueDate",
      dataIndex: "dueDate",
      render: (_, row) => row.dueDate ? formatDateOnly(row.dueDate.slice(0, ISO_DATE_LENGTH), units) : "—"
    },
    {
      title: text("Payment allocation item"),
      key: "allocationItem",
      sortValue: (row) => row.openItemDescription || row.contractName || "",
      render: (_, row) => {
        const status = paymentDisplayStatus(row);
        return (
          <Flex align="center" wrap gap={spacing.sm}>
            <Typography.Text>{row.openItemDescription || row.contractName || "—"}</Typography.Text>
            {status !== PAYMENT_DISPLAY_STATUS.paid ? renderPaymentStatus(row, text) : null}
          </Flex>
        );
      }
    },
    {
      title: text("Payment allocated amount"),
      key: "amount",
      dataIndex: "amount",
      align: "right",
      render: (_, row) => formatMoney(row.amount, units)
    }
  ];
}
